<template>
  <div v-if="shouldRender">
    <ais-instant-search
      :search-client="searchClient"
      :index-name="indexName"
      :insights="true"
      class="ais-search-results"
    >
      <ais-configure :hitsPerPage="hitsPerPage" />

      <!-- Stats and search input -->
      <div data-theme="light" class="module" ref="resourcesList">
        <div class="module-body">
          <!-- Statistics -->
          <ais-stats>
            <template v-slot="{ nbHits }">
              <p class="type-uppercase-small-regular">
                {{ sectionName }} /
                <span
                  >{{ nbHits }} search result{{ nbHits === 1 ? "" : "s" }}</span
                >
              </p>
            </template>
          </ais-stats>
          <!-- Search input -->
          <ais-search-box :value="query">
            <template v-slot:default="{ currentRefinement, refine }">
              <div class="ais-search-input">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="none"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M8 0a8 8 0 0 1 8 8c0 1.849-.63 3.549-1.683 4.903l5.39 5.39a1 1 0 1 1-1.414 1.414l-5.39-5.39A7.96 7.96 0 0 1 8 16 8 8 0 1 1 8 0m0 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2"
                  />
                </svg>
                <input
                  type="search"
                  :value="currentRefinement"
                  @input="
                    query = $event.currentTarget.value.toLowerCase();
                    refine($event.currentTarget.value.toLowerCase());
                  "
                  placeholder="Search resources"
                />
              </div>
            </template>
          </ais-search-box>
        </div>
      </div>

      <!-- Results -->
      <ais-hits>
        <template v-slot:default="{ items }">
          <!-- No results -->
          <div v-show="!items.length">
            <slot />
          </div>
          <!-- Results -->
          <section
            v-show="items.length"
            data-theme="light"
            class="module resources-index"
          >
            <div class="module-body">
              <ol class="resources-index-all">
                <li v-for="item in items" :key="item.objectID">
                  <a :href="'/' + item.url" class="entry-card hover-bg-grow">
                    <figure aria-hidden="true">
                      <img
                        :srcset="getSourceSet(item)"
                        sizes="
                              (min-width: 1680px) 491px,
                              (min-width: 1024px) 30vw,
                              (min-width: 768px) 45vw,
                              calc(100vw - 32px)
                          "
                        alt=""
                      />
                    </figure>
                    <span
                      class="type-p-3-semibold entry-card-meta"
                      aria-hidden="true"
                    >
                      <span class="resource-type">{{ getType(item) }}</span>
                      <template v-if="shouldShowDate(item)">
                        <time>{{ item.date }}</time>
                        &bull;
                      </template>
                      <span
                        >{{ item.consumptionTime }}
                        {{ item.consumptionTimeMeasurement }}
                      </span>
                    </span>
                    <h2 class="type-h5">{{ item.headline }}</h2>
                    <p aria-hidden="true" v-if="item.summary">
                      {{ item.summary }}
                    </p>
                  </a>
                </li>
              </ol>
            </div>
          </section>
        </template>
      </ais-hits>

      <!-- Pagination -->
      <ais-pagination
        v-slot="{ currentRefinement, nbPages, isFirstPage, isLastPage, refine }"
      >
        <nav
          v-show="nbPages > 1"
          data-theme="light"
          class="module pagination"
          aria-labelledby="pagination"
        >
          <div class="module-body">
            <h2 id="pagination" class="visuallyhidden">Pagination</h2>
            <ul class="type-p-3-semibold">
              <li>
                <a v-if="isFirstPage" href="#" @click.prevent disabled>
                  <IconArrowRight />
                  <span>Previous</span>
                </a>
                <a
                  v-else
                  href="#"
                  @click.prevent="
                    handlePageChange(refine, currentRefinement - 1)
                  "
                >
                  <IconArrowRight />
                  <span>Previous</span>
                </a>
              </li>
              <li
                v-for="(item, index) in getPagesToShow(
                  currentRefinement + 1,
                  nbPages,
                )"
                :key="index"
              >
                <span v-if="item === 'ellipsis'" class="pagination-truncated"
                  >…</span
                >
                <a
                  v-else
                  href="#"
                  @click.prevent="handlePageChange(refine, item - 1)"
                  :aria-current="item === currentRefinement + 1 ? 'page' : null"
                >
                  {{ item }}
                </a>
              </li>
              <li>
                <a v-if="isLastPage" href="#" @click.prevent disabled>
                  <span>Next</span>
                  <IconArrowRight />
                </a>
                <a
                  v-else
                  href="#"
                  @click.prevent="
                    handlePageChange(refine, currentRefinement + 1)
                  "
                >
                  <span>Next</span>
                  <IconArrowRight />
                </a>
              </li>
            </ul>
          </div>
        </nav>
      </ais-pagination>
    </ais-instant-search>
  </div>
</template>
<script setup>
import { ref, computed } from "vue";
import { liteClient } from "algoliasearch/lite";
import IconArrowRight from "./svg/IconArrowRight.vue";

const props = defineProps({
  applicationId: {
    type: String,
    default: "",
  },
  searchApiKey: {
    type: String,
    default: "",
  },
  indexPrefix: {
    type: String,
    default: "",
  },
  index: {
    type: String,
    default: "",
  },
  indexes: {
    type: [Array, Object],
    default: () => [],
  },
  initialQuery: {
    type: String,
    default: "",
  },
  sectionName: {
    type: String,
    default: "Resources",
  },
});

const resourcesList = ref(null);
const searchClient = liteClient(props.applicationId, props.searchApiKey);

const indexName = computed(() => {
  return `${props.indexPrefix}_${props.index}`;
});

const query = ref(props.initialQuery);
const hitsPerPage = 12;

const clearQuery = () => {
  query.value = "";
};

const handlePageChange = (refine, page) => {
  refine(page);
  // Scroll to the resourcesList element after pagination changes
  if (resourcesList.value) {
    resourcesList.value.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const getSourceSet = (resource) => {
  return `${resource.thumbnailSmall} 800w, ${resource.thumbnailMedium} 1374w, ${resource.thumbnailLarge} 1920w`;
};

const getType = (resource) => {
  return resource.resTagOverride
    ? resource.resTagOverride
    : resource.sectionName;
};

const shouldShowDate = (resource) => {
  // Find the section config for this resource
  const sectionConfig = indexList.value.find(
    (item) => item.section === resource.sectionHandle,
  );
  return sectionConfig?.showDate ?? true; // Default to true if not found
};

const indexList = computed(() => {
  if (Array.isArray(props.indexes) && props.indexes.length > 0) {
    // Check if it's the new array-of-objects format
    if (typeof props.indexes[0] === "object" && props.indexes[0].index) {
      // New format: [{ label: 'Blog', index: 'blog', section: 'resourcesBlog', showDate: true }]
      return props.indexes.map((item) => ({
        index: item.index,
        label: item.label,
        section: item.section,
        showDate: item.showDate ?? true, // Default to true if not specified
      }));
    }
  } else if (
    typeof props.indexes === "object" &&
    !Array.isArray(props.indexes)
  ) {
    // Legacy object format: { 'blog': 'Blog', 'webinars': 'Webinars' }
    return Object.entries(props.indexes).map(([index, label]) => ({
      index,
      label,
      section: null,
      showDate: true, // Default to true for legacy format
    }));
  }
  return [];
});

// Check if all required props are provided and valid
const shouldRender = computed(() => {
  return !!(
    props.applicationId &&
    props.searchApiKey &&
    props.indexPrefix &&
    props.index
  );
});

// Pagination logic matching the Twig macro
const getPagesToShow = (currentPage, totalPages) => {
  const pagesToShow = [];

  if (currentPage <= 3) {
    // Pages 1-3: Show 1, 2, 3, 4, ..., last
    for (let i = 1; i <= Math.min(4, totalPages); i++) {
      pagesToShow.push(i);
    }
    if (totalPages > 4) {
      pagesToShow.push("ellipsis");
      pagesToShow.push(totalPages);
    }
  } else if (currentPage >= totalPages - 2) {
    // Last 3 pages: Show 1, ..., n-3, n-2, n-1, n
    pagesToShow.push(1);
    if (totalPages > 4) {
      pagesToShow.push("ellipsis");
    }
    for (let i = Math.max(1, totalPages - 3); i <= totalPages; i++) {
      if (i > 1) {
        pagesToShow.push(i);
      }
    }
  } else {
    // Middle pages: Show 1, ..., current-1, current, current+1, ..., last
    pagesToShow.push(1);
    pagesToShow.push("ellipsis");

    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      pagesToShow.push(i);
    }

    pagesToShow.push("ellipsis");
    pagesToShow.push(totalPages);
  }

  return pagesToShow;
};
</script>
